import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  FiPlus, FiTrash2, FiEdit2, FiCheck, FiX,
  FiMail, FiPhone, FiMapPin, FiClock, FiUser, FiMessageSquare, FiSearch
} from 'react-icons/fi';
import { isFluxDomain } from '../../utils/fluxTheme';
import './AgentPipelines.css';

/* ─── Constants ─────────────────────────────────────────────────────────────── */

const DEFAULT_COLUMNS = [
  { id: 'col-submitted',    title: 'Submitted' },
  { id: 'col-needs-info',   title: 'Needs Information' },
  { id: 'col-pending-docs', title: 'Pending Documents' },
  { id: 'col-completed',    title: 'Completed' },
  { id: 'col-lost',         title: 'Lost / Cancelled' },
];

const FLUX_COLORS = ['#3FA45F', '#2563eb', '#f59e0b', '#10b981', '#ef4444'];
const MA_COLORS   = ['#4f46e5', '#0891b2', '#f59e0b', '#10b981', '#ef4444'];

/* ─── Pure helpers ──────────────────────────────────────────────────────────── */

function makeLeadId(lead) {
  return (lead.leadEmail || lead.leadPhone || 'unknown') + '_' + (lead.timestamp || '');
}

function getInitials(str) {
  if (!str) return '??';
  const local = str.includes('@') ? str.split('@')[0] : str;
  const parts = local.split(/[._+\-]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.substring(0, 2).toUpperCase();
}

/**
 * Returns the best display name for a lead card.
 * Prefers firstName+lastName, then leadName if it's a real name (not the email-prefix fallback).
 * Falls back to leadEmail or leadPhone.
 */
function formatCardName(leadName, leadEmail, leadPhone, firstName, lastName) {
  // Prefer explicit first+last from GHL enrichment
  const first = (firstName || '').trim();
  const last  = (lastName  || '').trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  const emailPrefix = leadEmail ? leadEmail.split('@')[0] : '';
  const isRealName = leadName && leadName.trim() && leadName.trim() !== emailPrefix && leadName.trim() !== leadEmail;
  if (isRealName) return leadName.trim();
  return leadEmail || leadPhone || '—';
}

/** Initials from the formatted card name. */
function getCardInitials(leadName, leadEmail, leadPhone, firstName, lastName) {
  const first = (firstName || '').trim();
  const last  = (lastName  || '').trim();
  if (first || last) {
    const parts = [first, last].filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  }
  const emailPrefix = leadEmail ? leadEmail.split('@')[0] : '';
  const isRealName = leadName && leadName.trim() && leadName.trim() !== emailPrefix && leadName.trim() !== leadEmail;
  if (isRealName) {
    const parts = leadName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return leadName.trim().substring(0, 2).toUpperCase();
  }
  return getInitials(leadEmail || leadPhone || '');
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

/* ─── Main component ─────────────────────────────────────────────────────────── */

export default function AgentPipelines({ token }) {
  const isFlux = isFluxDomain();
  const navigate = useNavigate();
  const COLORS = isFlux ? FLUX_COLORS : MA_COLORS;
  const accentColor = COLORS[0];

  /* state */
  const [leads, setLeads]     = useState({});     // { leadId: leadData }
  const [columns, setColumns] = useState([]);     // [{ id, title, leadIds[] }]
  const [loading, setLoading] = useState(true);
  const [agentKey, setAgentKey] = useState('');
  const [saveStatus, setSaveStatus] = useState('');  // '', 'saving', 'saved', 'error'

  /* drag state */
  const [dragCard, setDragCard]         = useState(null);  // { leadId, fromColId }
  const [dragOverCol, setDragOverCol]   = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null);

  /* column editing */
  const [editColId, setEditColId]       = useState(null);
  const [editTitle, setEditTitle]       = useState('');

  /* delete modal */
  const [deleteModal, setDeleteModal]   = useState(null); // colId

  /* adding column */
  const [addingCol, setAddingCol]       = useState(false);

  /* search */
  const [searchQuery, setSearchQuery]   = useState('');
  const [newColTitle, setNewColTitle]   = useState('');

  /* debounce timer for API saves */
  const saveTimerRef = useRef(null);

  /* ── resolve agent storage key from JWT ── */
  useEffect(() => {
    if (!token) return;
    try {
      const d = jwtDecode(token);
      setAgentKey(d.memberEmail || d.email || 'default');
    } catch (_) {}
  }, [token]);

  /* ── initial fetch – load board from API (fall back to localStorage) ── */
  useEffect(() => {
    if (!agentKey) return;

    const loadBoard = async () => {
      // 1. Try to load board state from server (cross-device sync)
      try {
        const boardRes = await axios.get('/api/agent/pipeline-board', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (boardRes.data.success && boardRes.data.board) {
          const serverBoard = boardRes.data.board;
          // Keep localStorage in sync as a fast cache
          localStorage.setItem(`pipeline_${agentKey}`, JSON.stringify(serverBoard));
          await fetchLeads(serverBoard);
          return;
        }
      } catch (err) {
        console.warn('[Pipeline] Could not load board from API, using localStorage fallback:', err.message);
      }

      // 2. Fallback: localStorage cache
      const cached = localStorage.getItem(`pipeline_${agentKey}`);
      await fetchLeads(cached ? JSON.parse(cached) : null);
    };

    loadBoard();
    // eslint-disable-next-line
  }, [agentKey]);

  async function fetchLeads(savedBoard) {
    setLoading(true);
    try {
      // Fetch all pages
      let allLeads = [];
      let page = 1;
      const limit = 500;
      while (true) {
        const res = await axios.get(`/api/agent/distribution/leads?page=${page}&limit=${limit}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const pageLeads = res.data.leads || [];
        allLeads = [...allLeads, ...pageLeads];
        if (!res.data.pagination?.hasMore) break;
        page++;
        if (page > 20) break; // safety cap at 10,000 leads
      }
      const raw = allLeads;

      const leadMap = {};
      raw.forEach(l => { leadMap[makeLeadId(l)] = l; });
      setLeads(leadMap);

      if (savedBoard && savedBoard.columns && savedBoard.columns.length) {
        /* restore, syncing in new leads and pruning deleted ones */
        const known = new Set(savedBoard.columns.flatMap(c => c.leadIds));
        const fresh = Object.keys(leadMap).filter(id => !known.has(id));
        const synced = savedBoard.columns.map((col, i) => ({
          ...col,
          leadIds: i === 0
            ? [...col.leadIds.filter(id => leadMap[id]), ...fresh]
            : col.leadIds.filter(id => leadMap[id])
        }));
        setColumns(synced);
      } else {
        /* first time: all leads land in first column */
        const all = Object.keys(leadMap);
        setColumns(DEFAULT_COLUMNS.map((col, i) => ({
          ...col, leadIds: i === 0 ? all : []
        })));
      }
    } catch (err) {
      console.error('Pipeline – error fetching leads:', err);
      setColumns(DEFAULT_COLUMNS.map(col => ({ ...col, leadIds: [] })));
    } finally {
      setLoading(false);
    }
  }

  /* ── persist on every column change – localStorage immediately, API debounced ── */
  useEffect(() => {
    if (!agentKey || columns.length === 0) return;

    // Immediate cache in localStorage (fast, offline-friendly)
    const boardData = { columns };
    localStorage.setItem(`pipeline_${agentKey}`, JSON.stringify(boardData));

    // Debounced save to database (1.5 s, cross-device sync)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');

    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(
          '/api/agent/pipeline-board',
          { columns },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaveStatus('saved');
        // Clear 'saved' indicator after 2 s
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (err) {
        console.warn('[Pipeline] Failed to save board to DB:', err.message);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    }, 1500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line
  }, [columns, agentKey]);

  /* ═══════════════════════════ DRAG & DROP ══════════════════════════════════ */

  function onDragStart(e, leadId, fromColId) {
    setDragCard({ leadId, fromColId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', leadId);
  }

  function onDragEnd() {
    setDragCard(null);
    setDragOverCol(null);
    setDragOverCard(null);
  }

  function onColDragOver(e, colId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCol !== colId) setDragOverCol(colId);
  }

  function onCardDragOver(e, colId, overLeadId) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colId);
    if (dragOverCard !== overLeadId) setDragOverCard(overLeadId);
  }

  function onColDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCol(null);
      setDragOverCard(null);
    }
  }

  function dropCard(e, toColId, beforeLeadId = null) {
    e.preventDefault();
    if (!dragCard) return;
    const { leadId, fromColId } = dragCard;

    setColumns(prev => prev.map(col => {
      if (col.id === fromColId) {
        return { ...col, leadIds: col.leadIds.filter(id => id !== leadId) };
      }
      if (col.id === toColId) {
        const without = col.leadIds.filter(id => id !== leadId);
        if (beforeLeadId) {
          const idx = without.indexOf(beforeLeadId);
          without.splice(idx >= 0 ? idx : without.length, 0, leadId);
          return { ...col, leadIds: without };
        }
        return { ...col, leadIds: [...without, leadId] };
      }
      return col;
    }));

    setDragCard(null);
    setDragOverCol(null);
    setDragOverCard(null);
  }

  /* ═══════════════════════════ COLUMN MANAGEMENT ═════════════════════════════ */

  function startEditCol(col) {
    setEditColId(col.id);
    setEditTitle(col.title);
  }

  function saveEditCol(colId) {
    const t = editTitle.trim();
    if (t) setColumns(prev => prev.map(c => c.id === colId ? { ...c, title: t } : c));
    setEditColId(null);
  }

  function requestDeleteCol(colId) {
    const col = columns.find(c => c.id === colId);
    if (col && col.leadIds.length > 0) {
      // ⛔ Block — column has leads, show blocking message
      setDeleteModal({ colId, blocked: true });
    } else {
      // ✅ Empty column — still show confirm modal before deleting
      setDeleteModal({ colId, blocked: false });
    }
  }

  function doDeleteCol(colId) {
    const col = columns.find(c => c.id === colId);
    setColumns(prev => {
      const rest = prev.filter(c => c.id !== colId);
      if (rest.length > 0 && col && col.leadIds.length > 0) {
        rest[0] = { ...rest[0], leadIds: [...rest[0].leadIds, ...col.leadIds] };
      }
      return rest;
    });
    setDeleteModal(null);
  }

  function addColumn() {
    const t = newColTitle.trim();
    if (!t) return;
    setColumns(prev => [...prev, { id: `col-${Date.now()}`, title: t, leadIds: [] }]);
    setNewColTitle('');
    setAddingCol(false);
  }

  /* ═══════════════════════════ RENDER ════════════════════════════════════════ */

  if (loading) {
    return (
      <div className="pl-wrapper">
        <div className="pl-page-header">
          <h2 className="pl-title">Pipelines</h2>
        </div>
        <div className="pl-loading">
          <div className="pl-spinner" style={{ borderTopColor: accentColor }}></div>
          <p>Loading your pipeline…</p>
        </div>
      </div>
    );
  }

  const totalLeads = Object.keys(leads).length;

  /* ── Search filter ── */
  const searchLower = searchQuery.trim().toLowerCase();
  const filteredColumns = searchLower
    ? columns.map(col => ({
        ...col,
        leadIds: col.leadIds.filter(leadId => {
          const lead = leads[leadId];
          if (!lead) return false;
          const name = formatCardName(lead.leadName, lead.leadEmail, lead.leadPhone, lead.firstName, lead.lastName).toLowerCase();
          const email = (lead.leadEmail || '').toLowerCase();
          const phone = (lead.leadPhone || '').toLowerCase();
          return name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
        })
      }))
    : columns;
  const searchMatchCount = searchLower ? filteredColumns.reduce((s, c) => s + c.leadIds.length, 0) : null;

  /* save status label */
  const saveLabel = saveStatus === 'saving'
    ? '⏳ Saving…'
    : saveStatus === 'saved'
    ? '✓ Saved'
    : saveStatus === 'error'
    ? '⚠ Save failed (changes cached locally)'
    : '';

  return (
    <div className="pl-wrapper">
      {/* Page header */}
      <div className="pl-page-header">
        <div>
          <h2 className="pl-title">Pipelines</h2>
          <p className="pl-subtitle">
            {searchLower ? `${searchMatchCount} of ${totalLeads}` : totalLeads} lead{totalLeads !== 1 ? 's' : ''} across {columns.length} column{columns.length !== 1 ? 's' : ''}
            &nbsp;·&nbsp; Drag cards to move them between stages
            {saveLabel && (
              <span
                className="pl-save-status"
                style={{
                  marginLeft: 12,
                  fontSize: 12,
                  color: saveStatus === 'error' ? '#ef4444'
                       : saveStatus === 'saved' ? '#16a34a'
                       : '#64748b'
                }}
              >
                {saveLabel}
              </span>
            )}
          </p>
        </div>
        <button
          className="pl-add-col-btn"
          onClick={() => setAddingCol(true)}
          style={{ background: accentColor }}
        >
          <FiPlus size={16} />
          Add Column
        </button>
      </div>

      {/* Search bar */}
      <div className="pl-search-bar">
        <div className="pl-search-input-wrap">
          <FiSearch size={15} className="pl-search-icon" />
          <input
            type="text"
            className="pl-search-input"
            placeholder="Search leads by name, email or phone…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="pl-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
              <FiX size={14} />
            </button>
          )}
        </div>
        {searchLower && (
          <span className="pl-search-count">
            {searchMatchCount} match{searchMatchCount !== 1 ? 'es' : ''} across all columns
          </span>
        )}
      </div>

      {/* Board */}
      <div className="pl-board">

        {filteredColumns.map((col, ci) => {
          const color = COLORS[ci % COLORS.length];
          const isDragTarget = dragOverCol === col.id;

          return (
            <div
              key={col.id}
              className={`pl-column${isDragTarget ? ' pl-column--over' : ''}`}
              style={{ '--col-color': color }}
              onDragOver={e => onColDragOver(e, col.id)}
              onDragLeave={onColDragLeave}
              onDrop={e => dropCard(e, col.id)}
            >
              {/* Column header */}
              <div className="pl-col-header">
                <div className="pl-col-header-left">
                  <span className="pl-col-dot" style={{ background: color }} />
                  {editColId === col.id ? (
                    <input
                      autoFocus
                      className="pl-col-title-input"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter')  saveEditCol(col.id);
                        if (e.key === 'Escape') setEditColId(null);
                      }}
                      onBlur={() => saveEditCol(col.id)}
                    />
                  ) : (
                    <span
                      className="pl-col-title"
                      onDoubleClick={() => startEditCol(col)}
                      title="Double-click to rename"
                    >
                      {col.title}
                    </span>
                  )}
                  <span className="pl-col-badge" style={{ background: color }}>
                    {col.leadIds.length}
                  </span>
                </div>

                <div className="pl-col-actions">
                  {editColId === col.id ? (
                    <>
                      <button className="pl-icon-btn pl-icon-btn--green" onClick={() => saveEditCol(col.id)} title="Save">
                        <FiCheck size={13} />
                      </button>
                      <button className="pl-icon-btn" onClick={() => setEditColId(null)} title="Cancel">
                        <FiX size={13} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="pl-icon-btn" onClick={() => startEditCol(col)} title="Rename column">
                        <FiEdit2 size={13} />
                      </button>
                      <button className="pl-icon-btn pl-icon-btn--red" onClick={() => requestDeleteCol(col.id)} title="Delete column">
                        <FiTrash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Cards list */}
              <div className="pl-cards">
                {col.leadIds.map(leadId => {
                  const lead = leads[leadId];
                  if (!lead) return null;
                  const contact   = lead.leadEmail || lead.leadPhone || '—';
                  const isEmail   = !!lead.leadEmail;
                  const displayName = formatCardName(lead.leadName, lead.leadEmail, lead.leadPhone, lead.firstName, lead.lastName);
                  const initials  = getCardInitials(lead.leadName, lead.leadEmail, lead.leadPhone, lead.firstName, lead.lastName);
                  const hasRealName = displayName !== contact; // true when showing a real name
                  const isDragging = dragCard?.leadId === leadId;
                  const isOverCard = dragOverCard === leadId;

                  return (
                    <div
                      key={leadId}
                      className={`pl-card${isDragging ? ' pl-card--dragging' : ''}${isOverCard && !isDragging ? ' pl-card--over' : ''}`}
                      draggable
                      onDragStart={e => onDragStart(e, leadId, col.id)}
                      onDragEnd={onDragEnd}
                      onDragOver={e => onCardDragOver(e, col.id, leadId)}
                      onDrop={e => { e.stopPropagation(); dropCard(e, col.id, leadId); }}
                      style={{ '--col-color': color }}
                    >
                      {/* Colored top accent bar */}
                      <div className="pl-card-accent" style={{ background: color }} />

                      <div className="pl-card-body">
                        {/* Avatar */}
                        <div className="pl-avatar" style={{ background: color }}>
                          {initials}
                        </div>

                        {/* Info */}
                        <div className="pl-card-info">
                          {/* Name / Contact */}
                          <div className="pl-card-contact" title={contact}>
                            <span className="pl-card-contact-text" style={{ fontWeight: hasRealName ? 600 : 400 }}>
                              {displayName}
                            </span>
                          </div>
                          {/* Show email/phone as secondary line when a real name is displayed */}
                          {hasRealName && (
                            <div className="pl-card-contact" style={{ marginTop: '2px', opacity: 0.65 }} title={contact}>
                              {isEmail
                                ? <FiMail size={11} className="pl-card-ico" />
                                : <FiPhone size={11} className="pl-card-ico" />}
                              <span className="pl-card-contact-text" style={{ fontSize: '11px' }}>{contact}</span>
                            </div>
                          )}

                          {/* Meta row */}
                          <div className="pl-card-meta">
                            {lead.state && (
                              <span className="pl-pill pl-pill--state">
                                <FiMapPin size={10} /> {lead.state}
                              </span>
                            )}
                            <span className="pl-pill pl-pill--date">
                              <FiClock size={10} /> {fmtDate(lead.timestamp)}
                            </span>
                          </div>

                          {/* Agent (group member view) */}
                          {lead.agentEmail && (
                            <div className="pl-card-agent">
                              <FiUser size={10} />
                              <span title={lead.agentEmail}>{lead.agentEmail}</span>
                            </div>
                          )}
                        </div>

                        {/* Chat icon — only when lead has an email */}
                        {lead.leadEmail && (
                          <button
                            title="Open conversation"
                            onClick={e => {
                              e.stopPropagation();
                              navigate('/agent/leads', { state: { openEmail: lead.leadEmail } });
                            }}
                            style={{
                              flexShrink: 0,
                              alignSelf: 'center',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '6px',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'color 0.15s, background 0.15s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = color; e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.background = 'none'; }}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Empty drop zone */}
                {col.leadIds.length === 0 && (
                  <div className="pl-empty">
                    <span>Drop leads here</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Inline "Add column" form */}
        {addingCol && (
          <div className="pl-column pl-column--new">
            <div className="pl-col-header">
              <input
                autoFocus
                className="pl-col-title-input"
                placeholder="Column title…"
                value={newColTitle}
                onChange={e => setNewColTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter')  addColumn();
                  if (e.key === 'Escape') { setAddingCol(false); setNewColTitle(''); }
                }}
              />
            </div>
            <div className="pl-new-col-btns">
              <button
                className="pl-btn-primary"
                style={{ background: accentColor }}
                onClick={addColumn}
                disabled={!newColTitle.trim()}
              >
                Add
              </button>
              <button
                className="pl-btn-secondary"
                onClick={() => { setAddingCol(false); setNewColTitle(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Delete column modal ── */}
      {deleteModal && (() => {
        const col = columns.find(c => c.id === deleteModal.colId);
        const leadCount = col?.leadIds.length || 0;
        return (
          <div className="pl-modal-overlay" onClick={() => setDeleteModal(null)}>
            <div className="pl-modal" onClick={e => e.stopPropagation()}>
              {deleteModal.blocked ? (
                /* ⛔ Blocked — column has leads */
                <>
                  <div className="pl-modal-icon">🚫</div>
                  <h3 className="pl-modal-title">Cannot Delete Column</h3>
                  <p className="pl-modal-body">
                    <strong>&ldquo;{col?.title}&rdquo;</strong> still has{' '}
                    <strong>{leadCount} lead{leadCount !== 1 ? 's' : ''}</strong> in it.
                    Move or reassign all leads to another column first, then delete.
                  </p>
                  <div className="pl-modal-actions">
                    <button className="pl-btn-secondary" onClick={() => setDeleteModal(null)}>
                      Got It
                    </button>
                  </div>
                </>
              ) : (
                /* ✅ Empty column — confirm delete */
                <>
                  <div className="pl-modal-icon">⚠️</div>
                  <h3 className="pl-modal-title">Delete Column?</h3>
                  <p className="pl-modal-body">
                    Are you sure you want to delete <strong>&ldquo;{col?.title}&rdquo;</strong>? This cannot be undone.
                  </p>
                  <div className="pl-modal-actions">
                    <button className="pl-btn-secondary" onClick={() => setDeleteModal(null)}>
                      Cancel
                    </button>
                    <button
                      className="pl-btn-danger"
                      onClick={() => doDeleteCol(deleteModal.colId)}
                    >
                      <FiTrash2 size={14} /> Delete Column
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
